/**
 * Life4Earth V2 — Project Taxonomy API Adapter
 *
 * CRUD for the project taxonomy tree (categories → types → subtypes).
 *
 * Backend contract:
 *   GET    /api/ProjectTaxonomy           → TaxonomyNode[]
 *   GET    /api/ProjectTaxonomy/{guid}    → TaxonomyNode
 *   POST   /api/ProjectTaxonomy           → TaxonomyNode (created)
 *   PUT    /api/ProjectTaxonomy/{guid}    → TaxonomyNode (updated)
 *   DELETE /api/ProjectTaxonomy/{guid}    → { deleted: number }
 *   POST   /api/ProjectTaxonomy/seed      → { created, total }
 */

import { apiFetch } from './client';

// ── Types ────────────────────────────────────────────────────────────

export type TaxonomyLevel = 'category' | 'type' | 'subtype';

/** Per-locale translation map: { ar: "...", fr: "...", ... } */
export type TaxonomyTranslations = Record<string, string>;

export interface TaxonomyNode {
  taxonomyGuid: string;
  level: TaxonomyLevel;
  parentTaxonomyGuid: string | null;
  label: string;
  sortOrder: number;
  isActive: boolean;
  kpiProfileGuid: string | null;
  templateGuid: string | null;
  /** Per-locale translations of the label. Empty object = English only. */
  translations: TaxonomyTranslations;
}

export interface CreateTaxonomyRequest {
  level: TaxonomyLevel;
  parentTaxonomyGuid?: string | null;
  label: string;
  sortOrder?: number;
  isActive?: boolean;
  translations?: TaxonomyTranslations;
}

export interface UpdateTaxonomyRequest {
  label?: string;
  sortOrder?: number;
  isActive?: boolean;
  kpiProfileGuid?: string | null;
  templateGuid?: string | null;
  translations?: TaxonomyTranslations;
}

// ── API Calls ────────────────────────────────────────────────────────

/** Fetch the full taxonomy tree (all nodes). */
export async function getTaxonomyNodes(): Promise<TaxonomyNode[]> {
  return apiFetch<TaxonomyNode[]>('/api/ProjectTaxonomy');
}

/** Fetch a single node by taxonomy_guid. */
export async function getTaxonomyNode(guid: string): Promise<TaxonomyNode> {
  return apiFetch<TaxonomyNode>(`/api/ProjectTaxonomy/${encodeURIComponent(guid)}`);
}

/** Create a new taxonomy node. */
export async function createTaxonomyNode(req: CreateTaxonomyRequest): Promise<TaxonomyNode> {
  return apiFetch<TaxonomyNode>('/api/ProjectTaxonomy', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
}

/** Update an existing taxonomy node (partial update). */
export async function updateTaxonomyNode(guid: string, req: UpdateTaxonomyRequest): Promise<TaxonomyNode> {
  return apiFetch<TaxonomyNode>(`/api/ProjectTaxonomy/${encodeURIComponent(guid)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
}

/** Delete a taxonomy node and its descendants. */
export async function deleteTaxonomyNode(guid: string): Promise<{ deleted: number }> {
  return apiFetch<{ deleted: number }>(`/api/ProjectTaxonomy/${encodeURIComponent(guid)}`, {
    method: 'DELETE',
  });
}

/** Seed the taxonomy table from hardcoded defaults (idempotent). */
export async function seedTaxonomy(): Promise<{ created: number; total: number }> {
  return apiFetch<{ created: number; total: number }>('/api/ProjectTaxonomy/seed', {
    method: 'POST',
  });
}
